import { skyFlightCourseTelemetry } from '../../../packages/domain/src/113-sky-flight-courses.mjs';
import { SKY_WORLDS } from '../../../packages/domain/src/75-sky-rider.mjs';
import { skyEquipment } from '../../../packages/domain/src/77-sky-upgrades.mjs';
import { skyBonusStatus } from '../../../packages/domain/src/82-sky-bonuses.mjs';

/** Compact temporary-power telemetry; accessible text retains every active
 * power while the single HUD line shows at most three countdowns. Seconds are
 * rounded up so an active sub-second effect never appears already expired. */
export function skyBonusTelemetry(bonuses) {
  const icons = {
    power: '✦ POWER',
    rapid: '» RAPID',
    shield: '◇ SHIELD',
    boost: '↑ BOOST',
    focus: '◎ FOCUS',
  };
  const active = bonuses.filter((bonus) => Number.isFinite(bonus.remaining) && bonus.remaining > 0);
  return {
    text:
      active
        .slice(0, 3)
        .map(
          (bonus) =>
            `${icons[bonus.key] ?? bonus.label} ${bonus.queued ? 'READY' : Math.ceil(bonus.remaining) + 's'}`,
        )
        .join(' · ') + (active.length > 3 ? ` · +${active.length - 3}` : ''),
    label: active
      .map(
        (bonus) =>
          `${bonus.label}: ${bonus.queued ? 'ready for the next combat opportunity' : Math.ceil(bonus.remaining) + ' seconds remaining'}`,
      )
      .join('. '),
  };
}

/** Own live flight telemetry and bounded feedback timers; active simulation time
 * freezes presentation on pause and reset clears counters between expeditions. */
export function createSkyFlightHud({
  telemetry,
  progress,
  precision,
  precisionLabel,
  precisionMeter,
  precisionImpact,
  feedback,
  flash,
  reduced,
}) {
  let visualScore = 0,
    visualLives = 3,
    feedbackUntil = 0,
    impactUntil = 0,
    visualEvent = 0,
    visualImpact = 0,
    precisionUntil = 0;
  function reset() {
    visualScore = 0;
    visualLives = 3;
    feedbackUntil = impactUntil = precisionUntil = visualImpact = visualEvent = 0;
    feedback.textContent = '';
  }
  function paint(state, playing, world) {
    const course = skyFlightCourseTelemetry(state);
    const encounterFraction = state.boss ? 1 - state.boss.hp / state.boss.maxHp : 0;
    const approachFraction = state.journey
      ? Math.min(1, state.journey.defeated / Math.max(1, state.journey.requiredKills))
      : Math.min(1, state.sectorTime / world.approachSeconds);
    const stageFraction =
      world.kind === 'bonus'
        ? (course?.progress ?? 0)
        : world.kind === 'rush'
          ? (state.encounter + encounterFraction) / 7
          : state.boss
            ? 0.6 + 0.4 * encounterFraction
            : 0.6 * approachFraction;
    progress.value = Math.min(SKY_WORLDS.length, state.sector + stageFraction);
    const bonuses = skyBonusTelemetry(skyBonusStatus(state));
    const flightTelemetry = `STAGE ${Math.min(SKY_WORLDS.length, state.sector + 1)}/${SKY_WORLDS.length} · SHOTS ${state.shotsFired ?? 0} · CANNON ${(state.upgrades?.weapon ?? 0) + 1} · ${state.salvage ?? 0} SALVAGE`;
    const courseText = course && !course.complete ? course.text : null;
    const courseLabel = courseText ? course.label : null;
    telemetry.textContent = bonuses.text || courseText || flightTelemetry;
    telemetry.dataset.mode = bonuses.text ? 'bonus' : 'flight';
    telemetry.setAttribute('aria-label', bonuses.label || courseLabel || flightTelemetry);
    telemetry.setAttribute('title', bonuses.label || courseLabel || flightTelemetry);
    precision.hidden = !playing || world.kind === 'bonus';
    const boosting = (state.overdriveTime ?? 0) > 0;
    const recovering = (state.overdriveRecovery ?? 0) > 0;
    precision.dataset.mode = boosting ? 'boost' : recovering ? 'recovery' : 'charging';
    precisionLabel.textContent = boosting
      ? `OVERDRIVE ×2 · ${state.overdriveTime.toFixed(1)}s`
      : recovering
        ? `COOLING · ${state.overdriveRecovery.toFixed(1)}s`
        : `PRECISION ${state.overdriveCharge ?? 0}/6`;
    precisionMeter.max = boosting ? skyEquipment(state.upgrades).overdriveSeconds : 6;
    precisionMeter.value = boosting ? state.overdriveTime : (state.overdriveCharge ?? 0);
    precisionMeter.setAttribute(
      'aria-label',
      boosting ? 'Overdrive duration remaining' : 'Precision hits toward overdrive',
    );
    if (state.lastImpact && state.lastImpact.id !== visualImpact) {
      visualImpact = state.lastImpact.id;
      const quality = state.lastImpact.quality;
      precisionImpact.textContent =
        quality === 'armor'
          ? 'ARMOR · AIM FOR CORE'
          : `${quality === 'critical' ? 'CORE HIT' : 'HIT'} · ${Math.round((state.lastImpact.energy ?? 1) * 100)}% POWER`;
      precisionImpact.dataset.quality = quality;
      precisionUntil = state.time + 0.7;
    }
    precisionImpact.hidden = state.time >= precisionUntil;
    const clear = state.lastSquadClear;
    const celebrating = clear && state.time >= clear.time && state.time < clear.time + 2.4;
    feedback.dataset.reward = celebrating ? (clear.firstPass ? 'perfect' : 'wave') : '';
    if (celebrating) {
      feedback.textContent = `${clear.firstPass ? '✦ FIRST PASS' : '◇ RETURN CLEAR'} · +${clear.score} POINTS${clear.powerUp ? ' · POWER-UP READY' : ''}`;
      feedbackUntil = clear.time + 2.4;
    } else if (
      state.eventId !== visualEvent &&
      [
        'squad-clear',
        'bonus',
        'formation-return',
        'boss-supercharge-ready',
        'course-complete',
      ].includes(state.eventKind)
    ) {
      feedback.textContent = state.event;
      feedbackUntil = state.time + 1.6;
    } else if (state.score > visualScore && state.eventKind !== 'course-ring') {
      feedback.textContent = `+${(state.score - visualScore).toLocaleString()}${state.combo > 1 ? ` · ${state.combo}× CHAIN` : ''}`;
      feedbackUntil = state.time + 0.85;
    }
    if (state.lives < visualLives) impactUntil = state.time + 0.3;
    visualEvent = state.eventId;
    visualScore = state.score;
    visualLives = state.lives;
    feedback.hidden = !playing || state.time >= feedbackUntil || Boolean(state.boss && celebrating);
    flash.hidden = !playing || reduced() || state.time >= impactUntil;
    feedback.style.setProperty(
      '--sky-feedback-life',
      String(Math.min(1, Math.max(0, (feedbackUntil - state.time) / 0.85))),
    );
  }
  return { paint, reset };
}
